<template>
  <nuxt-fetch ref="fetcher" :fetch-fn="fetchFn">
    <template #default="{ data }">
      <div>
        <script type="application/ld+json" v-html="jsonLd" />
        <!-- Mobile Header: breadcrumb, titre, ref, notation -->
        <div class="lg:hidden px-4 pt-2 big-container mx-auto">
          <breadcrumb
            :value="getBreadcrumb(data)"
            no-enriched
            class="mb-2 text-xs"
          />
          <div class="text-xl font-bold">{{ data.label }}</div>
          <div class="flex justify-between items-center mt-1">
            <small class="text-sm text-sky_grey font-bold">#{{ data.sku }}</small>
            <div v-if="data.properties.reviews" class="flex items-center text-sm">
              <span class="mr-2">{{ formatRating(data.properties.reviews.avg) }}</span>
              <notation :notation="data.properties.reviews.avg" :max="5" />
              <a href="#" class="ml-2 hover:underline" @click.stop.prevent="scrollToReviews">({{ data.properties.reviews.total }} avis)</a>
            </div>
          </div>
        </div>
        <div class="big-container mx-auto mb-5 flex flex-wrap flex-col lg:flex-row lg:px-4">
          <pictures-gallery
            :product="data"
          />
          <!-- Mobile Price + Add to Cart -->
          <div class="lg:hidden w-full px-4 mb-4">
            <price-dashboard
              :quantity="mobileQuantity"
              :product="data"
              class="mb-4"
            />
            <add-to-cart-input
              v-if="data.properties.stock_status !== 'out_of_stock'"
              :quantity.sync="mobileQuantity"
              :loading="mobileAddingToCart"
              :pcb="data.pricing.pcb"
              @add="(qty) => addToCartMobile(data, qty)"
            />
            <add-out-of-stock-email
              v-else
              :product-id="data.sku"
            />
          </div>
          <product-dashboard
            :product="data"
          />
        </div>
        <product-body :product="data" />
      </div>
    </template>
    <template #fetching>
      <div style="height: 900px;" />
    </template>
  </nuxt-fetch>
</template>

<script>
import NuxtFetch from '@hammerbot/nuxt-fetch'
import maxBy from 'lodash.maxby'
import PicturesGallery from './PicturesGallery'
import ProductDashboard from './ProductDashboard'
import ProductBody from './ProductBody'
import Notation from './Notation'
import AddToCartInput from './AddToCartInput'
import AddOutOfStockEmail from './AddOutOfStockEmail'
import PriceDashboard from './PriceDashboard'
import Breadcrumb from '../general/Breadcrumb'

export default {
  components: {
    PicturesGallery,
    ProductDashboard,
    ProductBody,
    NuxtFetch,
    Notation,
    AddToCartInput,
    AddOutOfStockEmail,
    PriceDashboard,
    Breadcrumb
  },
  props: {
    product: {
      required: true,
      default: null,
      type: Object
    }
  },
  data () {
    return {
      fetchedCount: 0,
      mobileQuantity: this.product?.pricing?.pcb || 1,
      mobileAddingToCart: false
    }
  },
  computed: {
    jsonLd () {
      const images = []
      if (this.product.cover) {
        images.push(this.$helpers.makeCdnUrl(this.product.cover, this.product.slug, 'c', 'b'))
      }
      if (this.product.properties?.secondaryPictures?.length) {
        images.push(...this.product.properties.secondaryPictures.map((si, siIndex) => {
          return this.$helpers.makeCdnUrl(si, this.product.slug, siIndex, 'b')
        }))
      }
      const reviewToUse = maxBy(this.product.reviews?.hits || [], 'rating')
      const obj = {
        '@context': 'http://schema.org',
        '@type': 'Product',
        sku: this.product.sku,
        image: images,
        name: this.product.properties?.metaName || this.product.label,
        aggregateRating: this.product.properties?.reviews?.total
          ? {
              '@type': 'AggregateRating',
              ratingValue: this.product.properties?.reviews?.avg?.toFixed(2),
              reviewCount: this.product.properties?.reviews?.total
            }
          : undefined,
        description: this.product.properties?.metaDescription,
        mpn: this.product.sku,
        brand: {
          '@type': 'Thing',
          name: 'Skylantern Original'
        },
        offers: [
          {
            '@type': 'Offer',
            priceCurrency: 'EUR',
            price: (this.product.pricing.unit_taxed_price).toFixed(2),
            itemCondition: 'http://schema.org/NewCondition',
            availability: this.product.stock > 0 ? 'http://schema.org/InStock' : 'https://schema.org/PreOrder',
            url: 'https://www.skylantern.fr' + this.$helpers.makeUri(this.product.path),
            seller: {
              '@type': 'Organization',
              name: 'Skylantern FR'
            }
          }
        ],
        review: {
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: 5
          },
          author: {
            '@type': 'Person',
            name: 'Annie L.'
          },
          reviewBody: 'Totalement satisfaite '
        }
      }
      if (reviewToUse) {
        Object.assign(obj, {
          review: {
            '@type': 'Review',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: reviewToUse.rating
            },
            author: {
              '@type': 'Person',
              name: reviewToUse.customer_name
            },
            reviewBody: reviewToUse.review
          }
        })
      }
      return JSON.stringify(obj, null, 2)
    }
  },
  watch: {
    '$store.state.auth.preferences' () {
      this.fetchedCount++
      this.$refs.fetcher.$fetch()
    }
  },
  mounted () {
    this.$gtmEvents.onProductDetailsView(this.product)
  },
  methods: {
    async fetchFn () {
      if (!this.fetchedCount) {
        return this.product
      }
      const { data } = await this.$api.get(`/products/${this.product.sku}`)
      return data
    },
    formatRating (avg) {
      if (avg === 5) {
        return '5'
      }
      return avg?.toFixed(1) || '0'
    },
    scrollToReviews () {
      document.querySelector('#reviews')?.scrollIntoView({
        behavior: 'smooth'
      })
    },
    getBreadcrumb (product) {
      return [
        ...product.hierarchy.map((hierarchy) => {
          return {
            text: hierarchy.label,
            to: this.$helpers.makeUri(hierarchy.path, 'category')
          }
        }),
        {
          text: product.properties.breadcrumb || product.label,
          to: this.$helpers.makeUri(product.path),
          disabled: true
        }
      ]
    },
    async addToCartMobile (product, quantity) {
      this.mobileAddingToCart = true
      try {
        const { price } = await this.$store.dispatch('cart/addProduct', {
          sku: product.sku,
          quantity,
          cover: product.cover,
          label: product.label,
          pricing: product.pricing,
          properties: {
            path: product.path,
            uri: this.$helpers.makeUri(product.path),
            background: product.properties.background,
            crossed_price: this.$store.getters['auth/paysTaxes'] ? product.pricing.crossed_taxed_price : product.pricing.crossed_price
          }
        })
        this.$gtmEvents.onAddedToCart(product, quantity, price, 'Fiche Produit')
        this.$store.commit('cart/setShow', true)
      } catch (error) {
        this.$gtmEvents.onRuntimeError(error, 'product/add-to-cart')
        // GTM peut être lui-même indisponible : on trace côté serveur.
        if (this.$trace) {
          this.$trace('ajout_panier_echec', {
            origine: 'fiche-produit-mobile',
            sku: product && product.sku,
            message: (error && error.message) || String(error)
          })
        }
      }
      this.mobileAddingToCart = false
    }
  }
}
</script>
